import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Play, Pause, Lock, ShoppingCart, Crown, Filter, Shirt, Package, Music2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import AnimatedSection from "@/components/AnimatedSection";
import { getFileUrl } from "@/components/FileUpload";
import { useCheckout } from "@/components/CheckoutModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PREVIEW_LIMIT = 30;
const MUSIC_TYPES = new Set(["music", "single", "ep", "album"]);
const DIGITAL_TYPES = new Set(["music", "single", "ep", "album", "digital"]);
const FILTER_TABS = [
  { key: "all", label: "All" },
  { key: "music", label: "Music" },
  { key: "clothing", label: "Clothing" },
  { key: "merch", label: "Merch" },
  { key: "accessories", label: "Accessories" },
  { key: "digital", label: "Digital" },
];

function isDigitalProduct(p) {
  if (p.is_digital === true) return true;
  if (p.is_digital === false) return false;
  return DIGITAL_TYPES.has(p.product_type);
}

function MusicProductCard({ product, onBuy }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [locked, setLocked] = useState(false);
  const hasPreview = !!product.audio_preview_url;

  const stop = () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; } setPlaying(false); setProgress(0); };
  const togglePlay = () => {
    if (locked || !audioRef.current) return;
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { audioRef.current.play(); setPlaying(true); }
  };
  const onTimeUpdate = () => {
    if (!audioRef.current) return;
    const t = audioRef.current.currentTime;
    if (t >= PREVIEW_LIMIT) { audioRef.current.pause(); setPlaying(false); setLocked(true); setProgress(100); return; }
    const d = Math.min(audioRef.current.duration || PREVIEW_LIMIT, PREVIEW_LIMIT);
    setProgress(d > 0 ? (t / d) * 100 : 0);
  };

  return (
    <div className={`bg-[#0A0A0A] border overflow-hidden group transition-all duration-500 ${
      product.exclusive ? "border-[#8B0000]/30 hover:border-[#8B0000]/60 shadow-[0_0_20px_rgba(139,0,0,0.1)]" : "border-white/5 hover:border-[#D4AF37]/20"
    }`}>
      {hasPreview && (
        <audio ref={audioRef} src={getFileUrl(product.audio_preview_url)} onTimeUpdate={onTimeUpdate}
          onEnded={() => { setLocked(true); setPlaying(false); setProgress(100); }} />
      )}

      <div className="relative aspect-square overflow-hidden">
        {product.image_url ? (
          <img src={getFileUrl(product.image_url)} alt={product.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        ) : (
          <div className="w-full h-full bg-[#111111]" />
        )}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-500" />

        {/* Play button on hover */}
        {hasPreview && !locked && (
          <button data-testid={`play-btn-${product.id}`} onClick={togglePlay}
            className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300 ${
              playing ? "bg-[#D4AF37] scale-100 opacity-100" : "bg-[#D4AF37]/90 scale-90 opacity-0 group-hover:scale-100 group-hover:opacity-100"
            }`}>
            {playing ? <Pause className="w-7 h-7 text-black" /> : <Play className="w-7 h-7 text-black ml-1" />}
          </button>
        )}

        {/* Lock overlay */}
        {locked && (
          <div data-testid={`lock-overlay-${product.id}`} className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center z-20 backdrop-blur-sm">
            <Lock className="w-6 h-6 text-[#8B0000] mb-2" />
            <p className="text-sm font-semibold text-white/80 mb-1">Unlock Full Track</p>
            <button onClick={() => { setLocked(false); stop(); }} className="text-[10px] text-[#D4AF37] uppercase tracking-wider font-semibold border border-[#D4AF37]/30 px-4 py-1.5 mt-2 hover:border-[#D4AF37]">Replay Preview</button>
          </div>
        )}

        {/* Exclusive banner */}
        {product.exclusive && (
          <div data-testid={`exclusive-badge-${product.id}`} className="absolute top-0 left-0 right-0 z-30">
            <div className="bg-gradient-to-r from-[#8B0000] via-[#B22222] to-[#8B0000] px-4 py-2 flex items-center justify-center gap-2">
              <Crown className="w-3.5 h-3.5 text-[#D4AF37]" />
              <span className="text-[11px] uppercase tracking-[0.25em] font-bold text-white">Exclusive</span>
              <Crown className="w-3.5 h-3.5 text-[#D4AF37]" />
            </div>
          </div>
        )}

        {/* Progress bar during playback */}
        {hasPreview && playing && !locked && (
          <div className="absolute bottom-0 left-0 right-0 z-20 px-4 pb-3 pt-8 bg-gradient-to-t from-black/80 to-transparent">
            <div className="h-1 bg-white/15 rounded-full"><div className="h-full bg-[#D4AF37] rounded-full transition-all" style={{ width: `${Math.min(progress, 100)}%` }} /></div>
          </div>
        )}
      </div>

      <div className="p-5">
        <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] mb-1">{product.artist_name || "Luxifer 6"}</p>
        <h3 data-testid={`product-title-${product.id}`} className="font-heading text-xl leading-tight truncate mb-1">{product.title}</h3>
        {product.description && <p className="text-white/35 text-xs mb-4 line-clamp-2">{product.description}</p>}

        <div className="flex items-center justify-between mb-4">
          <span data-testid={`product-price-${product.id}`} className="text-xl font-bold text-[#D4AF37]">${(product.price || 0).toFixed(2)}</span>
          {product.featured && <span className="text-[9px] uppercase bg-[#D4AF37] text-black px-2 py-0.5 rounded font-bold">Featured</span>}
        </div>

        <div className="flex gap-2">
          {hasPreview && !locked && (
            <Button data-testid={`preview-btn-${product.id}`} onClick={togglePlay} variant="outline"
              className={`text-xs h-10 uppercase tracking-wider font-semibold transition-all duration-300 ${
                playing ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 text-white/50 hover:border-[#D4AF37]/50 hover:text-[#D4AF37]"
              }`}>
              {playing ? <><Pause className="w-3.5 h-3.5 mr-1.5" /> Playing</> : <><Play className="w-3.5 h-3.5 mr-1.5" /> Preview</>}
            </Button>
          )}
          <Button data-testid={`buy-btn-${product.id}`}
            onClick={() => onBuy(product.id)}
            className="flex-1 bg-[#D4AF37] text-black hover:bg-white text-xs h-10 uppercase tracking-wider font-semibold">
            <ShoppingCart className="w-3.5 h-3.5 mr-1.5" /> Buy Now
          </Button>
        </div>
      </div>
    </div>
  );
}

function GeneralProductCard({ product, onBuy }) {
  return (
    <div className="bg-[#0A0A0A] border border-white/5 hover:border-[#D4AF37]/20 overflow-hidden group transition-all duration-500">
      <div className="relative aspect-square overflow-hidden">
        {product.image_url ? (
          <img src={getFileUrl(product.image_url)} alt={product.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        ) : (
          <div className="w-full h-full bg-[#111111] flex items-center justify-center">
            {product.product_type === "clothing" ? <Shirt className="w-10 h-10 text-white/10" /> : <Package className="w-10 h-10 text-white/10" />}
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

        {/* Type badge */}
        <div className="absolute top-3 left-3 z-10">
          <span className="text-[10px] uppercase tracking-wider bg-white/10 backdrop-blur-sm text-white/70 px-2.5 py-1 rounded font-semibold capitalize">{product.product_type}</span>
        </div>

        {product.stock >= 0 && product.stock <= 5 && (
          <div className="absolute top-3 right-3 z-10">
            <span className="text-[10px] uppercase tracking-wider bg-[#8B0000] text-white px-2 py-1 rounded font-bold">
              {product.stock === 0 ? "Sold Out" : `${product.stock} Left`}
            </span>
          </div>
        )}
      </div>

      <div className="p-5">
        <h3 data-testid={`product-title-${product.id}`} className="font-heading text-xl leading-tight truncate mb-1">{product.title}</h3>
        {product.description && <p className="text-white/35 text-xs mb-4 line-clamp-2">{product.description}</p>}

        <div className="flex items-center justify-between mb-4">
          <span data-testid={`product-price-${product.id}`} className="text-xl font-bold text-[#D4AF37]">${(product.price || 0).toFixed(2)}</span>
          {product.featured && <span className="text-[9px] uppercase bg-[#D4AF37] text-black px-2 py-0.5 rounded font-bold">Featured</span>}
        </div>

        <Button data-testid={`order-btn-${product.id}`}
          onClick={() => { if (product.stock !== 0) onBuy(product.id); }}
          disabled={product.stock === 0}
          className={`w-full text-xs h-10 uppercase tracking-wider font-semibold ${
            product.stock === 0
              ? "bg-white/10 text-white/30 cursor-not-allowed"
              : "bg-[#D4AF37] text-black hover:bg-white"
          }`}>
          <ShoppingCart className="w-3.5 h-3.5 mr-1.5" /> {product.stock === 0 ? "Sold Out" : "Order Now"}
        </Button>
      </div>
    </div>
  );
}

export default function Store() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const { startCheckout, modal } = useCheckout();

  useEffect(() => {
    axios.get(`${API}/store/products?_t=${Date.now()}`).then(r => { setProducts(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const merchEnabled = products.length > 0 ? !!products[0]?.merch_enabled : false;

  // When merch is OFF, only show digital products
  const visibleProducts = merchEnabled ? products : products.filter(p => isDigitalProduct(p));

  const filtered = filter === "all" ? visibleProducts : visibleProducts.filter(p => {
    if (filter === "music") return isDigitalProduct(p);
    return p.product_type === filter;
  });

  // Get unique types that exist for filter tabs
  const existingTypes = new Set(visibleProducts.map(p => {
    if (MUSIC_TYPES.has(p.product_type)) return "music";
    return p.product_type;
  }));
  const tabs = FILTER_TABS.filter(t => t.key === "all" || existingTypes.has(t.key));

  return (
    <div className="pt-16">
      <div className="relative py-24 sm:py-32 px-6 bg-gradient-to-b from-[#D4AF37]/5 to-transparent">
        <div className="max-w-6xl mx-auto">
          <p className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-3">Official Merch</p>
          <h1 data-testid="store-page-title" className="font-heading text-5xl sm:text-6xl lg:text-7xl">STORE</h1>
          <p className="text-white/50 mt-4 max-w-lg">Support the movement. Own the music. Wear the brand.</p>
        </div>
      </div>

      <div className="px-6 pb-24">
        <div className="max-w-6xl mx-auto">
          {/* Filter tabs */}
          {tabs.length > 2 && (
            <div data-testid="store-filter-bar" className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
              {tabs.map(t => (
                <button key={t.key} data-testid={`filter-${t.key}-btn`}
                  onClick={() => setFilter(t.key)}
                  className={`text-xs uppercase tracking-wider font-semibold px-4 py-2 border whitespace-nowrap transition-all duration-300 ${
                    filter === t.key
                      ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10"
                      : "border-white/10 text-white/40 hover:border-white/20 hover:text-white/60"
                  }`}>
                  {t.label}
                </button>
              ))}
            </div>
          )}

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => <div key={i} className="bg-[#111111] border border-white/5 aspect-[3/4] animate-pulse" />)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-white/30">
              <ShoppingCart className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p className="text-lg">{products.length === 0 ? "Store opening soon." : "No products in this category."}</p>
              {filter !== "all" && <button onClick={() => setFilter("all")} className="text-sm mt-2 text-[#D4AF37] hover:text-white transition-colors">View all products</button>}
            </div>
          ) : (
            <div data-testid="store-products-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((product, index) => (
                <AnimatedSection key={product.id} testId={`store-product-${product.id}`} delay={index * 80}>
                  {isDigitalProduct(product) ? (
                    <MusicProductCard product={product} onBuy={startCheckout} />
                  ) : (
                    <GeneralProductCard product={product} onBuy={startCheckout} />
                  )}
                </AnimatedSection>
              ))}
            </div>
          )}
        </div>
      </div>
      {modal}
    </div>
  );
}
